import { useEffect, useRef } from 'react';

export type RefreshAuthToken = () => Promise<string | null> | string | null | void;

interface UseAuthRefreshParams {
  fetchCurrentUser: () => void;
  refreshAuthToken?: RefreshAuthToken;
  onNoSession: () => void;
}

const hasLocalStorage = () => typeof localStorage !== 'undefined';

const getStoredTokens = () => {
  if (!hasLocalStorage()) {
    return { authToken: null, refreshToken: null };
  }

  return {
    authToken: localStorage.getItem('authToken'),
    refreshToken: localStorage.getItem('refreshToken'),
  };
};

export const useAuthRefresh = ({
  fetchCurrentUser,
  refreshAuthToken,
  onNoSession,
}: UseAuthRefreshParams) => {
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      if (!hasLocalStorage()) {
        if (isMounted) onNoSession();
        return;
      }

      const { authToken, refreshToken } = getStoredTokens();

      if (refreshToken && refreshAuthToken) {
        const refreshedToken = await refreshAuthToken();
        if (refreshedToken) {
          fetchCurrentUser();
          return;
        }
        if (isMounted) onNoSession();
        return;
      }

      if (authToken) {
        fetchCurrentUser();
        return;
      }

      if (isMounted) onNoSession();
    };

    initializeAuth();
    return () => {
      isMounted = false;
    };
  }, [fetchCurrentUser, onNoSession, refreshAuthToken]);

  useEffect(() => {
    if (typeof window === 'undefined' || !refreshAuthToken) return;

    const handleResume = async () => {
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;
      try {
        const refreshedToken = await refreshAuthToken();
        if (refreshedToken) {
          fetchCurrentUser();
        }
      } finally {
        isRefreshingRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleResume();
      }
    };

    window.addEventListener('focus', handleResume);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleResume);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchCurrentUser, refreshAuthToken]);
};
